import axios from "axios";
const API_BASE_URL = '/api-proxy/';

export const s3Axios = axios.create();

const getToken = () => localStorage.getItem('token');

const publicEndpoints = ['/signin', '/forget-password', '/reset-password', '/site-details/details', '/license'];

const isTokenExpired = (token) => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp && payload.exp * 1000 < Date.now();
    } catch {
        return false;
    }
};

export const CHANGE_PASSWORD_PATH = '/change-password';

const clearSessionAndRedirect = (reason) => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin_details');
    localStorage.removeItem('refresh_token');
    if (reason) {
        // Read once by the login screen so the user learns why they were kicked out.
        sessionStorage.setItem('session_end_reason', reason);
    }
    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
};

/**
 * Forced change-password gate. While `must_change_password` is set the backend
 * 403s every route except update-password and get-profile, so this redirect has
 * to fire from anywhere in the app, on any request — that is also what blocks
 * the "bypass by typing a URL" case.
 */
const redirectToChangePassword = () => {
    if (window.location.pathname !== CHANGE_PASSWORD_PATH) {
        window.location.href = CHANGE_PASSWORD_PATH;
    }
};

axios.interceptors.response.use(
    (response) => {
        const body = response.data;
        if (body && typeof body === 'object' && 'success' in body) {
            if (body.message && !body.responseMessage) {
                body.responseMessage = body.message;
            }
            const inner = body.data;
            if (inner !== undefined && inner !== null) {
                if (Array.isArray(inner)) {
                    body.responseDetails = inner;
                } else if (typeof inner === 'object' && 'rows' in inner) {
                    const rows = inner.rows || [];
                    const enriched = [...rows];
                    enriched.data = rows;
                    enriched.totalRecords = inner.total;
                    enriched.recordsTotal = inner.total;
                    enriched.recordsFiltered = inner.total;
                    body.responseDetails = enriched;
                } else if (typeof inner === 'object') {
                    body.responseDetails = inner;
                }
            }
        }
        return response;
    },
    (error) => {
        if (error.response) {
            const status = error.response.status;
            if (typeof error.response.data !== 'object' || error.response.data === null) {
                error.response.data = {};
            }
            const errBody = error.response.data;
            if (errBody.message && !errBody.responseMessage) {
                errBody.responseMessage = errBody.message;
            }
            if (errBody.responseMessage && typeof errBody.responseMessage === 'string') {
                let msg = errBody.responseMessage;
                
                const transformations = [
                    { pattern: /is_hero_banner_eligible/gi, replacement: 'Hero Banner Eligibility' },
                    { pattern: /is_free/gi, replacement: 'Free status' },
                    { pattern: /is_recommended/gi, replacement: 'Recommended status' },
                    { pattern: /is_active/gi, replacement: 'Active status' },
                    { pattern: /category_id/gi, replacement: 'Category' },
                    { pattern: /tag_id/gi, replacement: 'Genre' },
                    { pattern: /type_id/gi, replacement: 'Tag' },
                    { pattern: /total_episode/gi, replacement: 'Total Episodes' },
                    { pattern: /free_episodes/gi, replacement: 'Free Episodes' },
                    { pattern: /publish_at/gi, replacement: 'Publish Date' },
                    { pattern: /unpublish_at/gi, replacement: 'Unpublish Date' },
                    { pattern: /age_rating/gi, replacement: 'Age Rating' },
                    { pattern: /cover_video/gi, replacement: 'Cover Video' },
                    { pattern: /"/g, replacement: '' },
                    { pattern: /must be one of \[0, 1\]/gi, replacement: 'must be Yes or No' },
                    { pattern: /must be one of \[(.*?)\]/gi, replacement: (match, p1) => `must be one of: ${p1}` }
                ];

                transformations.forEach(({ pattern, replacement }) => {
                    msg = msg.replace(pattern, replacement);
                });
                
                if (msg.includes(';')) {
                    const parts = msg.split(';').map(p => p.trim());
                    const filteredParts = parts.filter((part) => {
                        if (part.includes('must be a number') && parts.some(p => p.includes('must be Yes or No') || p.includes('must be one of'))) {
                            return false;
                        }
                        return true;
                    });
                    msg = filteredParts.join('; ');
                }
                errBody.responseMessage = msg;
            }
            if (status === 413) {
                if (!errBody.responseMessage && !errBody.message) {
                    errBody.responseMessage = 'Request payload too large. Please try again.';
                }
            } else if (status === 503 || status === 502) {
                errBody.responseMessage = 'Service is temporarily unavailable. Please try again later.';
            } else if (status === 500) {
                errBody.responseMessage = 'The server encountered an error. Please try again later.';
            }
            // Branch on errorType, not on the message string — messages get reworded.
            const errorType = errBody.errorType;

            if (status === 403 && errorType === 'PasswordChangeRequiredError') {
                redirectToChangePassword();
            }

            if (status === 401) {
                const requestUrl = error.config?.url || '';
                const isPublicEndpoint = publicEndpoints.some(ep => requestUrl.includes(ep));
                const currentToken = getToken();
                if (!isPublicEndpoint && currentToken) {
                    // A permissions_version bump (role edited / reassigned / deleted)
                    // invalidates the session server-side. Don't retry, don't refresh —
                    // the refresh token doesn't help, re-login is the only path.
                    const reason = errorType === 'UnauthorizedError'
                        ? (errBody.responseMessage || 'Your access was updated. Please log in again.')
                        : null;
                    clearSessionAndRedirect(reason);
                }
            }
        } else if (error.request) {
            error.networkError = true;
            error.userMessage = 'Cannot reach the server. Please check your internet connection.';
        }
        return Promise.reject(error);
    }
);

axios.interceptors.request.use(
    (config) => {
        const token = getToken();
        if (token) {
            if (!config.headers['Authorization']) {
                config.headers['Authorization'] = `Bearer ${token}`;
            }
        }
        return config;
    }
);

export const login = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/signin`, data)
}

export const forgetPassword = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/forget-password`, data)
}

export const resetPassword = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/reset-password`, data)
}

export const verifyToken = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/verify-token`, data)
}

export const updateProfile = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/update-profile`, data)
}

export const getProfile = () => {
    return axios.get(`${API_BASE_URL}admin/v1/get-profile`)
}

export const updatePassword = (data) => {
    const payload = {
        old_password:     data.oldPassword     || data.old_password,
        new_password:     data.newPassword     || data.new_password,
        confirm_password: data.confirmPassword || data.confirm_password,
    };
    return axios.post(`${API_BASE_URL}admin/v1/update-password`, payload)
}

export const getDashboardDetails = () => {
    return axios.get(`${API_BASE_URL}admin/v1/dashboard`)
}

export const getSiteDetails = () => {
    return axios.get(`${API_BASE_URL}admin/v1/site-details/details`)
}

export const updateSiteDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/site-details/update`, data)
}

export const getSocialLinks = () => {
    return axios.get(`${API_BASE_URL}admin/v1/social-links/details`)
}

export const updateSocialLinks = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/social-links/update`, data)
}

export const getAppDetails = () => {
    return axios.get(`${API_BASE_URL}admin/v1/app-data/details`)
}

export const updateAppDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/app-data/update`, data)
}

export const updateAppCurrencyDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/app-data/update-currency`, data)
}

export const getCategories = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/category/get`, data)
}

export const getCategoryDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/category/details`, data)
}

export const addCategories = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/category/add`, data)
}

export const updateCategories = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/category/update`, data)
}

export const updateCategoryRanks = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/category/update-rank`, data)
}

export const getCategorySeriesList = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/category/series`, data)
}

export const updateCategorySeriesRanks = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/category/update-series-rank`, data)
}

export const deleteCategories = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/category/delete`, data)
}

export const getLanguages = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/language/get`, data)
}

export const getLanguageDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/language/details`, data)
}

export const addLanguages = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/language/add`, data)
}

export const updateLanguages = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/language/update`, data)
}

export const deleteLanguages = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/language/delete`, data)
}

export const getTags = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/tags/get`, data)
}

export const getTagDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/tags/details`, data)
}

export const addTags = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/tags/add`, data)
}

export const updateTags = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/tags/update`, data)
}

export const deleteTags = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/tags/delete`, data)
}

// ── User Segments (CI-958) ──────────────────────────────────────────────────
export const getUserSegments = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/user-segments/get`, data)
}

export const getUserSegmentDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/user-segments/details`, data)
}

// user_ids: array or comma / newline separated text (pasted or read from a CSV).
export const addUserSegment = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/user-segments/add`, data)
}

// user_ids replaces the segment's whole list.
export const updateUserSegment = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/user-segments/update`, data)
}

export const deleteUserSegment = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/user-segments/delete`, data)
}

// Body: { ordered_ids: [id, ...] } — priority genre ids in ascending rank order.
export const reorderTagsPriority = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/tags/reorder-priority`, data)
}

export const getTypes = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/types/get`, data)
}

export const getTypeDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/types/details`, data)
}

export const addTypes = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/types/add`, data)
}

export const updateTypes = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/types/update`, data)
}

export const deleteTypes = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/types/delete`, data)
}

export const getPaymentMethod = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/payment-methods/get`, data)
}

export const getPaymentMethodDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/payment-methods/details`, data)
}

export const addPaymentMethod = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/payment-methods/add`, data)
}

export const updatePaymentMethod = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/payment-methods/update`, data)
}

export const updatePaymentMethodStatus = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/payment-methods/update-status`, data)
}

export const deletePaymentMethod = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/payment-methods/delete`, data)
}

export const getPlans = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/plans/get`, data)
}

export const getPlanDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/plans/details`, data)
}

export const addPlans = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/plans/add`, data)
}

export const updatePlans = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/plans/update`, data)
}

export const deletePlans = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/plans/delete`, data)
}

export const updateIsActivePlan = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/plans/update-status`, data)
}

export const getVipPlanList = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/plans/get-vip`, data)
}

export const getSeries = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/series/get`, data)
}

export const getSeriesDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/series/details`, data)
}

export const addSeries = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/series/add`, data)
}

export const updateSeries = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/series/update`, data)
}

export const updateRecommandedSeries = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/series/update-recommended`, data)
}

export const updateActiveSeries = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/series/update-active`, data)
}

export const updateFreeSeries = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/series/update-free`, data)
}

export const deleteSeries = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/series/delete`, data)
}

export const getEpisodes = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/episode/seriesWise`, data)
}

export const getEpisodeStreamUrl = (episodeId) => {
    return axios.get(`${API_BASE_URL}admin/v1/episode/${episodeId}/stream-url`)
}

export const getEpisodePresignedUrl = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/episode/presigned-url`, data)
}

export const getSeriesPresignedUrl = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/series/presigned-url`, data)
}

export const getSeriesCoverVideoStatus = (id) => {
    return axios.get(`${API_BASE_URL}admin/v1/series/${id}/cover-video-status`)
}

export const getAllEpisodes = () => {
    return axios.get(`${API_BASE_URL}admin/v1/episode/get`)
}

export const getEpisodesDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/episode/details`, data)
}

export const addEpisodes = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/episode/add`, data)
}

export const addEpisodesUrl = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/episode/add-urls`, data)
}

export const updateEpisodes = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/episode/update`, data)
}

export const deleteEpisodes = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/episode/delete`, data)
}

export const getTranscodingStatus = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/episode/transcoding-status`, data)
}

export const getUsersList = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/users/list`, data)
}

export const getUsersDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/users/details`, data)
}

export const updateIsBlockedDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/users/update-blocked`, data)
}

export const getRewardHistoryList = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/users/reward-history`, data)
}

export const getPurchaseCoinHistoryList = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/users/coin-plan-history`, data)
}

export const getPurchaseVIPHistoryList = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/users/vip-plan-history`, data)
}

export const getAboutUs = () => {
    return axios.get(`${API_BASE_URL}admin/v1/about-us/details`)
}

export const updateAboutUs = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/about-us/update`, data)
}

export const getTermsCondition = () => {
    return axios.get(`${API_BASE_URL}admin/v1/terms-condition/details`)
}

export const updateTermsCondition = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/terms-condition/update`, data)
}

export const getPrivacyPolicy = () => {
    return axios.get(`${API_BASE_URL}admin/v1/privacy-policy/details`)
}

export const updatePrivacyPolicy = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/privacy-policy/update`, data)
}

export const getAdsPLatformList = () => {
    return axios.get(`${API_BASE_URL}admin/v1/ads/get`)
}

export const getAdsPlatformDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/ads/details`, data)
}

export const updateAdsPlatformDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/ads/update`, data)
}

export const updateAdsPlatformStatus = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/ads/update-status`, data)
}

export const getCoinPlanOrderList = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/coin-order-history/get`, data)
}

export const getVipPlanOrderList = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/coin-order-history/get-vip`, data)
}

export const getReportReason = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/report-reason/get`, data)
}

export const getReportReasonDetails = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/report-reason/details`, data)
}

export const addReportReason = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/report-reason/add`, data)
}

export const updateReportReason = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/report-reason/update`, data)
}

export const deleteReportReason = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/report-reason/delete`, data)
}

export const getTicketList = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/report-reason/get-tickets`, data)
}

export const updateTicketStatus = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/report-reason/update-ticket-status`, data)
}

export const getFAQ = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/faq/get`, data)
}

export const addFAQ = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/faq/add`, data)
}

export const updateFAQ = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/faq/update`, data)
}

export const deleteFAQ = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/faq/delete`, data)
}

export const sendNotification = (formData) => {
    return axios.post(`${API_BASE_URL}admin/v1/notification/send`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    })
}

export const getNotificationHistory = (page = 1, limit = 20) => {
    return axios.get(`${API_BASE_URL}admin/v1/notification?page=${page}&limit=${limit}`)
}

export const getNotificationAudienceStats = () => {
    return axios.get(`${API_BASE_URL}admin/v1/notification/audience-stats`)
}

export const cancelNotification = (id) => {
    return axios.post(`${API_BASE_URL}admin/v1/notification/${id}/cancel`)
}

export const deleteNotification = (id) => {
    return axios.delete(`${API_BASE_URL}admin/v1/notification/${id}`)
}

export const exportUsers = () => {
    return axios.get(`${API_BASE_URL}admin/v1/export/users`, { responseType: 'blob' })
}

export const exportTransactions = () => {
    return axios.get(`${API_BASE_URL}admin/v1/export/transactions`, { responseType: 'blob' })
}

export const exportSeries = () => {
    return axios.get(`${API_BASE_URL}admin/v1/export/series`, { responseType: 'blob' })
}

export const exportEpisodes = () => {
    return axios.get(`${API_BASE_URL}admin/v1/export/episodes`, { responseType: 'blob' })
}

export const enableDRM = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/drm/enable`, data)
}

export const disableDRM = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/drm/disable`, data)
}

export const getDRMStatus = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/drm/status`, data)
}

export const getSiteData = () => {
    return axios.get(`${API_BASE_URL}admin/v1/site-details/details`)
}

export const verifyLicense = (data) => {
    return axios.post(`${API_BASE_URL}v1/license`, data)
}

export const getLicense = (data) => {
    return axios.post(`${API_BASE_URL}v1/license/verify`, data)
}

export const getAnalytics = () => {
    return axios.get(`${API_BASE_URL}admin/v1/analytics/revenue`)
}

export const getDailyAnalytics = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/analytics/daily`, data)
}

export const getMonthlyAnalytics = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/analytics/monthly`, data)
}

export const getUserGrowth = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/analytics/user-growth`, data)
}

export const getRevenueAnalytics = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/analytics/revenue`, data)
}

export const getDauAnalytics = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/analytics/dau`, data)
}

export const getSeriesViewsAnalytics = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/analytics/series-views`, data)
}

export const getAnalyticsDashboard = (period = '30d') => {
    return axios.get(`${API_BASE_URL}admin/v1/analytics?period=${period}`)
}

export const getPlayerAnalytics = (period = '7d') => {
    return axios.get(`${API_BASE_URL}admin/v1/analytics/player?period=${period}`)
}

export const getRealtimeAnalytics = () => {
    return axios.get(`${API_BASE_URL}admin/v1/analytics/realtime`)
}

/* ── Team Members (/admin/v1/rbac/admin-users) ─────────────────────────────
 * The rbac router is mounted under /rbac, so these sit alongside the role
 * routes — the integration doc's §6.3 table omits the prefix, but the server
 * does not.
 */
export const getAdminUsers = () => {
    return axios.get(`${API_BASE_URL}admin/v1/rbac/admin-users`)
}

// role_id is mandatory — the backend rejects creation without one.
export const createAdminUser = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/rbac/admin-users`, data)
}

// Including role_id in the body makes this a Super-Admin-only call.
export const updateAdminUser = (id, data) => {
    return axios.put(`${API_BASE_URL}admin/v1/rbac/admin-users/${id}`, data)
}

export const deleteAdminUser = (id) => {
    return axios.delete(`${API_BASE_URL}admin/v1/rbac/admin-users/${id}`)
}

/**
 * Super Admin only. Sets a temporary password on the target admin and flips
 * their must_change_password flag. The backend does not generate the password —
 * `new_password` is required in the body, so the caller supplies it and is the
 * one who shares it with the user.
 */
export const resetAdminUserPassword = (id, new_password) => {
    return axios.post(`${API_BASE_URL}admin/v1/rbac/admin-users/${id}/reset-password`, { new_password })
}

/* ── Roles (/admin/v1/rbac/*) ─────────────────────────────────────────────── */

export const getRoles = () => {
    return axios.get(`${API_BASE_URL}admin/v1/rbac/roles`)
}

export const getRoleDetails = (id) => {
    return axios.get(`${API_BASE_URL}admin/v1/rbac/roles/${id}`)
}

export const createRole = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/rbac/roles`, data)
}

export const updateRole = (id, data) => {
    return axios.put(`${API_BASE_URL}admin/v1/rbac/roles/${id}`, data)
}

export const deleteRole = (id) => {
    return axios.delete(`${API_BASE_URL}admin/v1/rbac/roles/${id}`)
}

/** Catalogue of every permission row, used to resolve module+action → id. */
export const getPermissionCatalog = () => {
    return axios.get(`${API_BASE_URL}admin/v1/rbac/permissions`)
}

/**
 * Replaces a role's permission set. Bumps permissions_version for every admin
 * holding the role, force-expiring their sessions.
 */
export const updateRolePermissions = (id, data) => {
    return axios.put(`${API_BASE_URL}admin/v1/rbac/roles/${id}/permissions`, data)
}

/* ── Admin ↔ Role assignment (Super Admin only) ───────────────────────────── */

export const getAdminRoles = (adminId) => {
    return axios.get(`${API_BASE_URL}admin/v1/rbac/admins/${adminId}/roles`)
}

export const assignAdminRole = (adminId, data) => {
    return axios.put(`${API_BASE_URL}admin/v1/rbac/admins/${adminId}/roles`, data)
}

export const removeAdminRole = (adminId, roleId) => {
    return axios.delete(`${API_BASE_URL}admin/v1/rbac/admins/${adminId}/roles/${roleId}`)
}

export const getAdminPermissions = (adminId) => {
    return axios.get(`${API_BASE_URL}admin/v1/rbac/admins/${adminId}/permissions`)
}

/* ── Session ──────────────────────────────────────────────────────────────── */

export const refreshAccessToken = (refresh_token) => {
    return axios.post(`${API_BASE_URL}admin/v1/refresh-token`, { refresh_token })
}

export const getModerationReports = (params) => {
    const qs = new URLSearchParams(params).toString();
    return axios.get(`${API_BASE_URL}admin/v1/moderation/reports?${qs}`)
}

export const approveModerationReport = (id) => {
    return axios.post(`${API_BASE_URL}admin/v1/moderation/reports/${id}/approve`)
}

export const rejectModerationReport = (id, reason) => {
    return axios.post(`${API_BASE_URL}admin/v1/moderation/reports/${id}/reject`, { reason })
}

export const assignTicket = (id, admin_id) => {
    return axios.post(`${API_BASE_URL}admin/v1/report-reason/tickets/${id}/assign`, { admin_id })
}

export const getBanners = (data) => {
    return axios.get(`${API_BASE_URL}admin/v1/curation/banners`, { params: data })
}

export const getBannerStreamUrl = (bannerId) => {
    return axios.get(`${API_BASE_URL}admin/v1/curation/banners/${bannerId}/stream-url`)
}

export const getBannerTranscodingStatus = (bannerId) => {
    return axios.get(`${API_BASE_URL}admin/v1/curation/banners/${bannerId}/transcoding-status`, { timeout: 15000 })
}

export const getBannerPresignedUrl = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/curation/banners/presigned-url`, data)
}

export const createBanner = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/curation/banners`, data)
}

export const updateBanner = (id, data) => {
    return axios.put(`${API_BASE_URL}admin/v1/curation/banners/${id}`, data)
}

export const patchBanner = (id, data) => {
    return axios.patch(`${API_BASE_URL}admin/v1/curation/banners/${id}`, data)
}

export const deleteBanner = (id) => {
    return axios.delete(`${API_BASE_URL}admin/v1/curation/banners/${id}`)
}

export const searchSeriesForBanner = (params) => {
    return axios.get(`${API_BASE_URL}admin/v1/curation/banners/series-search`, { params })
}

export const getHeroBannerEligibleSeries = (data) => {
    return axios.get(`${API_BASE_URL}admin/v1/series/hero-banner-eligible`, { params: data })
}

export const updateHeroBannerEligible = (data) => {
    const { id, series_id, is_hero_banner_eligible } = data;
    return axios.post(`${API_BASE_URL}admin/v1/series/update-hero-banner-eligible`, {
        id: parseInt(id || series_id),
        is_hero_banner_eligible: parseInt(is_hero_banner_eligible)
    })
}

export const reorderBanners = (data) => {
    const items = data.items || data.banners || [];
    const mappedItems = items.map((item, index) => ({
        id: parseInt(item.id),
        position: item.position !== undefined ? parseInt(item.position) : (item.rank !== undefined ? parseInt(item.rank) : index)
    }));
    return axios.patch(`${API_BASE_URL}admin/v1/curation/banners/reorder`, { items: mappedItems })
}

export const getBannersWithSeries = (data) => {
    return axios.get(`${API_BASE_URL}admin/v1/curation/banners`, { params: data })
}

export const uploadBannerImage = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/upload/image`, data)
}

/**
 * Curated Collection — the single "Trending" rail on the app home page.
 *
 * A singleton config row (id is always "1"), so there is no create or delete:
 * GET reads it, PUT replaces all three fields at once. `series_ids` carries the
 * display order, 0-10 entries, and the backend rejects fewer than 5 while
 * `is_active` is true.
 */
export const getCuratedCollection = () => {
    return axios.get(`${API_BASE_URL}admin/v1/curation/curated-collection`)
}

// Params: { search?, page?, limit? } — `limit` maxes out at 50 server-side.
// Returns active series only, and does NOT exclude ones already in the
// collection, so the picker has to flag duplicates itself.
export const searchCuratedCollectionSeries = (params) => {
    return axios.get(`${API_BASE_URL}admin/v1/curation/curated-collection/series-search`, { params })
}

// Body: { name, is_active, series_ids: [id, ...] } — all three required.
export const saveCuratedCollection = (data) => {
    return axios.put(`${API_BASE_URL}admin/v1/curation/curated-collection`, data)
}

/**
 * Generic curation rails (e.g. `new_release` = New on Hike Cinema), picked by `curation_type`
 * — a stable slug, never a row id: query on GET, query + body on writes. Additive: Trending
 * keeps its own /curation/curated-collection route above.
 *
 * GET → { id, curation_type, name, is_active, series_ids, excluded_ids,
 *         reorder: [{ series_id, position, expires_at }], preview[] }
 * PUT/PATCH respond with the same shape, reflecting the saved state.
 */
export const getCuration = (curationType) => {
    return axios.get(`${API_BASE_URL}admin/v1/curation/curate-collections`, { params: { curation_type: curationType } })
}

// Full replace: name, is_active, series_ids and excluded_ids all required.
export const saveCuration = (data) => {
    return axios.put(`${API_BASE_URL}admin/v1/curation/curate-collections`, data, { params: { curation_type: data?.curation_type } })
}

// Body: { curation_type, curation_config_id, ...any of name / is_active /
// series_ids / excluded_ids / reorder }. Omitted fields keep their value, and
// `reorder` is a delta. series_ids AND excluded_ids both [] = full reset.
export const patchCuration = (data) => {
    return axios.patch(`${API_BASE_URL}admin/v1/curation/curate-collections`, data, { params: { curation_type: data?.curation_type } })
}

export const retryEpisodeTranscode = (episodeId) => {
    return axios.post(`${API_BASE_URL}admin/v1/episode/${episodeId}/retry`)
}

// ── Bulk Series Import (S3 source) ────────────────────────────────────────────

export const createBulkImportBatch = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/bulk-series-imports`, data)
}

export const checkBulkImportExistingSeries = (data) => {
    return axios.post(`${API_BASE_URL}admin/v1/bulk-series-imports/check-existing`, data)
}

export const validateBulkImportBatch = (id) => {
    return axios.post(`${API_BASE_URL}admin/v1/bulk-series-imports/${id}/validate`)
}

// Body in production: { confirm_series_count, confirm_episode_count } — must
// match GET /:id/start-summary, or the backend refuses the start.
export const startBulkImportBatch = (id, data) => {
    return axios.post(`${API_BASE_URL}admin/v1/bulk-series-imports/${id}/start`, data || {})
}

export const getBulkImportStartSummary = (id) => {
    return axios.get(`${API_BASE_URL}admin/v1/bulk-series-imports/${id}/start-summary`)
}

export const retryBulkImportEpisodes = (id, data) => {
    return axios.post(`${API_BASE_URL}admin/v1/bulk-series-imports/${id}/episodes/retry`, data)
}

export const getBulkImportAudit = (id) => {
    return axios.get(`${API_BASE_URL}admin/v1/bulk-series-imports/${id}/audit`)
}

export const retryBulkImportSeriesItems = (id, data) => {
    return axios.post(`${API_BASE_URL}admin/v1/bulk-series-imports/${id}/retry`, data)
}

export const cancelBulkImportBatch = (id) => {
    return axios.post(`${API_BASE_URL}admin/v1/bulk-series-imports/${id}/cancel`)
}

export const publishBulkImportSeries = (id, data) => {
    return axios.post(`${API_BASE_URL}admin/v1/bulk-series-imports/${id}/publish`, data)
}

export const browseBulkImportFolder = (params) => {
    return axios.get(`${API_BASE_URL}admin/v1/bulk-series-imports/browse`, {
        params: { ...params, _: Date.now() },
        headers: { 'Cache-Control': 'no-cache, no-store', Pragma: 'no-cache' },
    })
}

export const getBulkImportBatches = (params) => {
    return axios.get(`${API_BASE_URL}admin/v1/bulk-series-imports`, { params })
}

export const getBulkImportBatchDetail = (id) => {
    return axios.get(`${API_BASE_URL}admin/v1/bulk-series-imports/${id}`)
}

export const getBulkImportSeriesItems = (id, params) => {
    return axios.get(`${API_BASE_URL}admin/v1/bulk-series-imports/${id}/series`, { params })
}

export const getBulkImportEpisodeItems = (seriesItemId, params) => {
    return axios.get(`${API_BASE_URL}admin/v1/bulk-series-imports/series/${seriesItemId}/episodes`, { params })
}

export const downloadBulkImportReport = (id) => {
    return axios.get(`${API_BASE_URL}admin/v1/bulk-series-imports/${id}/report`, { responseType: 'blob' })
}

